import toast from "react-hot-toast";
import { Calendar } from 'lucide-react';
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "@ncdai/react-wheel-picker/dist/style.css";
import BeatLoader from "react-spinners/BeatLoader";
import { useEffect, useRef, useState, useMemo } from "react";
import { IoMdArrowBack, IoIosCheckmarkCircle } from "react-icons/io";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { WheelPicker, WheelPickerWrapper } from "@ncdai/react-wheel-picker";
import { signupStep1, signupStep2, signupVerifyAndCreate } from "../../redux/ApiSlice/Auth/SignupSlice";
import { AccountProfile, clearAccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { setClearHomepageVideos } from "../../redux/ApiSlice/HomePageVideoSlice";
import { persistor } from "../../redux/store";

const CreateAccountWithPhone = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const [phoneNumber, setPhoneNumber] = useState("");
    const [fullName, setFullName] = useState("");
    const [birthday, setBirthday] = useState("06-15-2000");
    const [password, setPassword] = useState("");
    const [showWheelPicker, setShowWheelPicker] = useState(false);

    const [pickerValue, setPickerValue] = useState({
        month: "Jun",
        day: "15",
        year: "2020",
    });

    const debounceRef = useRef(null);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumberOrSpecialChar: false,
    });

    const [termsAccepted, setTermsAccepted] = useState(false);
    const [stepOneError, setStepOneError] = useState("");

    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const otpInputRefs = useRef([]);
    const [otpTimer, setOtpTimer] = useState(300); // 5 minutes in seconds
    const timerRef = useRef(null);


    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const days = useMemo(() => Array.from({ length: 31 }, (_, i) => (i + 1).toString()), []);
    const years = useMemo(() => Array.from({ length: 75 }, (_, i) => (2025 - i).toString()), []);

    const monthOptions = useMemo(() => months.map((m) => ({ label: m, value: m })), []);
    const dayOptions = useMemo(() => days.map((d) => ({ label: d, value: d })), [days]);
    const yearOptions = useMemo(() => years.map((y) => ({ label: y, value: y })), [years]);


    const US_COUNTRY_CODE = "+1";

    const formatPhoneDisplay = (digits) => {
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handlePhoneChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
        setPhoneNumber(digits);
        if (stepOneError) setStepOneError("");
    };

    const handleBackClick = () => {
        if (step === 1) {
            navigate(-1);
        } else {
            setStep(step - 1);
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);

        setPasswordValidation({
            minLength: value.length >= 8,
            hasUpperCase: /[A-Z]/.test(value),
            hasLowerCase: /[a-z]/.test(value),
            hasNumberOrSpecialChar: /[0-9!@#$%^&*(),.?":{}|<>]/.test(value),
        });
    };

    const handleOtpChange = (index, value) => {
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        const newOtp = ["", "", "", "", "", ""];
        for (let i = 0; i < digits.length; i++) {
            newOtp[i] = digits[i];
        }
        setOtp(newOtp);
    };

    const handleResendCode = () => {
        setOtp(["", "", "", "", "", ""]);
        otpInputRefs.current[0]?.focus();
        if (timerRef.current) clearInterval(timerRef.current);
        setOtpTimer(120);
        timerRef.current = setInterval(() => {
            setOtpTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        handleSubmitStep2(true);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
        if (step === 4) {
            timerRef.current = setInterval(() => {
                setOtpTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            };
        }
    }, [step]);

    useEffect(() => {
        if (step === 4) {
            setOtp(["", "", "", "", "", ""]);
            setOtpTimer(120);
            setTimeout(() => {
                otpInputRefs.current[0]?.focus();
            }, 100);
        }
    }, [step]);

    const handleDateChange = (nextValue) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setPickerValue(nextValue);

            const monthIndex = months.indexOf(nextValue.month) + 1;
            const formattedMonth = monthIndex.toString().padStart(2, "0");
            const formattedDay = nextValue.day.padStart(2, "0");
            const formattedDate = `${formattedMonth}-${formattedDay}-${nextValue.year}`;
            setBirthday(formattedDate);
        }, 80);
    };

    const handleBirthdayInputChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 8);

        // Live formatting as MM-DD-YYYY while typing
        let formatted = digits;
        if (digits.length > 4) {
            formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
        } else if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
        }
        setBirthday(formatted);

        if (digits.length !== 8) return;

        const monthNum = Math.min(Math.max(parseInt(digits.slice(0, 2), 10), 1), 12);
        const yearRaw = digits.slice(4);
        const maxDays = new Date(parseInt(yearRaw) || parseInt(pickerValue.year), monthNum, 0).getDate();
        const dayNum = Math.min(Math.max(parseInt(digits.slice(2, 4), 10), 1), maxDays);
        const yearStr = years.includes(yearRaw) ? yearRaw : pickerValue.year;

        const paddedMonth = monthNum.toString().padStart(2, "0");
        const paddedDay = dayNum.toString().padStart(2, "0");
        const normalized = `${paddedMonth}-${paddedDay}-${yearStr}`;
        setBirthday(normalized);

        const monthLabel = months[monthNum - 1];
        if (monthLabel) {
            setPickerValue({
                month: monthLabel,
                day: dayNum.toString(),
                year: yearStr,
            });
        }
    };

    const handleChangePhone = () => {
        setStep(1);
        setStepOneError("");
    };

    const extractErrorMessage = (err, fieldName) => {
        if (!err) return "Something went wrong. Please try again.";
        if (typeof err === "string") return err;
        if (Array.isArray(err)) return err[0] || "Something went wrong. Please try again.";
        if (typeof err?.[fieldName] === "string") return err[fieldName];
        if (Array.isArray(err?.[fieldName])) return err[fieldName][0];
        if (err?.error || err?.detail || err?.message) return err.error || err.detail || err.message;
        const [firstKey] = Object.keys(err ?? {});
        const firstVal = firstKey ? err[firstKey] : undefined;
        if (typeof firstVal === "string") return firstVal;
        if (Array.isArray(firstVal)) return firstVal[0];
        return "Something went wrong. Please try again.";
    };

    // Format birthday from MM-DD-YYYY to MM/DD/YYYY for API
    const formatDobForApi = (dob) => dob.replace(/-/g, "/");

    const handleSubmitStep1 = async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        const payload = {
            phone: `${US_COUNTRY_CODE}${phoneNumber}`,
        };
        try {
            await dispatch(signupStep1(payload)).unwrap();
            setStepOneError("");
            setStep(2);
        } catch (err) {
            const msg = extractErrorMessage(err, "phone");
            setStepOneError(msg);
            toast.error(msg);
        } finally {
            if (!isSilent) setLoading(false);
        }
    };

    const handleSubmitStep2 = async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        const payload = {
            full_name: fullName,
            date_of_birth: formatDobForApi(birthday),
            password,
        };
        try {
            await dispatch(signupStep2(payload)).unwrap();
            toast.success("OTP sent to your phone");
            setStep(4);
        } catch (err) {
            const msg = extractErrorMessage(err);
            toast.error(msg);
        } finally {
            if (!isSilent) setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        setLoading(true);
        const otpValue = otp.join("");
        try {
            const result = await dispatch(signupVerifyAndCreate({ otp: otpValue })).unwrap();
            if (result?.access) {
                localStorage.setItem('accessToken', result.access);
                localStorage.setItem("new_User", true);
            }
            // Clear stale persisted Redux state from any previously logged-in account
            dispatch(clearAccountProfile());
            dispatch(setClearHomepageVideos());
            await persistor.purge();
            // Load the newly created user's profile
            await dispatch(AccountProfile());
            toast.success("Account created successfully!");
            sessionStorage.setItem("show_interests", "true");
            navigate("/user-intrest");
        } catch (err) {
            const msg = extractErrorMessage(err, "otp");
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const isPhoneValid = phoneNumber.length === 10;
    const isDobValid = (() => {
        const match = birthday.match(/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(19|20)\d\d$/);
        if (!match) return false;
        const [, mm, dd, yyyy] = match;
        const date = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
        if (date.getMonth() !== parseInt(mm) - 1 || date.getDate() !== parseInt(dd)) return false;
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - 13);
        return date <= cutoff;
    })();
    const isPasswordValid = Object.values(passwordValidation).every((v) => v);

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21]">
            <style>
                {`
                .dob-picker-mask {
                    background: linear-gradient(to bottom,
                        rgba(255,255,255,0.95) 0%,
                        rgba(255,255,255,0.7) 20%,
                        rgba(255,255,255,0) 40%,
                        rgba(255,255,255,0) 60%,
                        rgba(255,255,255,0.7) 80%,
                        rgba(255,255,255,0.95) 100%);
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .spinner { animation: spin 1s linear infinite; }
                .no-spinner::-webkit-outer-spin-button,
                .no-spinner::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
                .no-spinner { -moz-appearance: textfield; }
                [data-rwp-option] { opacity: 0.35; color: #6b7280; font-size: 14px; }
                [data-rwp-highlight-wrapper] { background: rgba(255, 255, 255, 0.9); }
                [data-rwp-highlight-item] { color: #111827; font-weight: 700; font-size: 16px; }
                `}
            </style>
            <div className="sticky top-0">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2 z-10">
                    <IoMdArrowBack
                        onClick={handleBackClick}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Create an account</p>
                </div>
            </div>

            {/* Step 1: Phone Number */}
            {step === 1 && (
                <div className="p-4">
                    <p className="text-[18px] font-semibold text-black dark:text-white">What's your phone number?</p>
                    <p className="text-[11px] mt-1 font-semibold text-gray-500 dark:text-gray-400">
                        We'll use this to secure your account.
                    </p>
                    <div className="px-4 pt-4 pb-3 rounded-xl border-2 border-[#BDE6FF] mt-5">
                        <p className="text-[12px] font-semibold text-black dark:text-white">Phone number</p>
                        <div className="flex items-center gap-2">
                            <span className="text-[12px] font-semibold text-black dark:text-white select-none">+1</span>
                            <input
                                type="tel"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={formatPhoneDisplay(phoneNumber)}
                                placeholder="234-567-8901"
                                onChange={handlePhoneChange}
                                className="flex-1 text-[12px] focus:outline-none text-black dark:text-white bg-white dark:bg-[#1E1D21]"
                            />
                        </div>
                    </div>
                    {stepOneError && <p className="mt-2 text-[11px] font-semibold text-red-500 dark:text-red-400">{stepOneError}</p>}
                    <button
                        onClick={() => handleSubmitStep1()}
                        disabled={!isPhoneValid || loading}
                        className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black rounded-xl w-full my-6 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loading ? <BeatLoader size={10} color={"#000"} loading={loading} /> : "Next"}
                    </button>
                </div>
            )}

            {/* Step 2: Name + Birthday */}
            {step === 2 && (
                <div className="p-4">
                    <p className="text-[18px] font-semibold text-black dark:text-white">Set up your profile</p>
                    <p className="text-[11px] mt-1 font-semibold text-gray-500 dark:text-gray-400">
                        This helps people recognize you.
                    </p>

                    <div className="px-4 pt-4 pb-3 rounded-xl border-2 border-[#BDE6FF] mt-5">
                        <p className="text-[12px] font-semibold text-black dark:text-white">Full name</p>
                        <input
                            type="text"
                            maxLength={250}
                            value={fullName}
                            placeholder="Your name"
                            onChange={(e) => setFullName(e.target.value)}
                            className="w-full text-[12px] focus:outline-none text-black dark:text-white bg-white dark:bg-[#1E1D21]"
                        />
                    </div>

                    <div className="px-4 pt-4 pb-3 rounded-xl border-2 border-[#BDE6FF] mt-5">
                        <p className="text-[12px] font-semibold text-black dark:text-white">Birthday</p>
                        <div className="relative">
                            <input
                                type="text"
                                value={birthday}
                                inputMode="numeric"
                                pattern="[0-9\\-]*"
                                onChange={handleBirthdayInputChange}
                                placeholder="MM / DD / YYYY"
                                className="w-full text-[12px] focus:outline-none text-black dark:text-white bg-white dark:bg-[#1E1D21]"
                            />
                            <Calendar
                                onClick={() => setShowWheelPicker(!showWheelPicker)}
                                className="absolute h-4 w-4 right-0 top-0.5 cursor-pointer text-gray-600 hover:text-gray-900 transition-colors duration-200"
                            />
                        </div>
                    </div>

                    <p className="text-[11px] mt-2 font-semibold text-gray-500 dark:text-gray-400 text-center">
                        You must be 13 or older to use Chaa Chingg.
                    </p>

                    {showWheelPicker && (
                        <div className="mt-4" style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}>
                            <WheelPickerWrapper className="w-full flex justify-center gap-3">
                                <WheelPicker
                                    options={monthOptions}
                                    value={pickerValue.month}
                                    onValueChange={(v) => handleDateChange({ ...pickerValue, month: v })}
                                    optionItemHeight={40}
                                    visibleCount={12}
                                    scrollSensitivity={5}
                                    dragSensitivity={3}
                                    className="min-w-[80px]"
                                />
                                <WheelPicker
                                    options={dayOptions}
                                    value={pickerValue.day}
                                    onValueChange={(v) => handleDateChange({ ...pickerValue, day: v })}
                                    optionItemHeight={40}
                                    visibleCount={12}
                                    scrollSensitivity={6}
                                    dragSensitivity={3}
                                    className="min-w-[60px]"
                                />
                                <WheelPicker
                                    options={yearOptions}
                                    value={pickerValue.year}
                                    onValueChange={(v) => handleDateChange({ ...pickerValue, year: v })}
                                    optionItemHeight={40}
                                    visibleCount={12}
                                    scrollSensitivity={8}
                                    dragSensitivity={3}
                                    className="min-w-[90px]"
                                />
                            </WheelPickerWrapper>
                        </div>
                    )}

                    <button
                        onClick={() => setStep(3)}
                        disabled={!fullName.trim() || !isDobValid}
                        className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black rounded-xl w-full my-6 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Step 3: Password + Terms → triggers API */}
            {step === 3 && (
                <div className="p-4">
                    <p className="text-[18px] font-semibold text-black dark:text-white">Create a password</p>
                    <p className="text-[11px] mt-1 font-semibold text-gray-500 dark:text-gray-400">
                        Make sure it's strong and secure.
                    </p>

                    <div className="px-4 pt-4 pb-3 rounded-xl border-2 border-[#BDE6FF] mt-5">
                        <p className="text-[12px] font-semibold text-black dark:text-white">Password</p>
                        <div className="relative">
                            <input
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="Enter your password"
                                type={showPassword ? "text" : "password"}
                                className="w-full text-[12px] focus:outline-none text-black dark:text-white bg-white dark:bg-[#1E1D21]"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                            >
                                {showPassword ? (
                                    <AiOutlineEyeInvisible className="text-[20px]" />
                                ) : (
                                    <AiOutlineEye className="text-[20px]" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="mt-3 ml-5 space-y-1">
                        <div className="flex items-center gap-x-1">
                            <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.minLength ? "text-cyan-500 dark:text-cyan-400" : "text-gray-300"}`} />
                            <p className={`text-[11px] ${passwordValidation.minLength ? "text-cyan-500 dark:text-cyan-400 font-semibold" : "text-gray-600 dark:text-gray-400"}`}>At least 8 characters</p>
                        </div>
                        <div className="flex items-center gap-x-1">
                            <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.hasUpperCase ? "text-cyan-500 dark:text-cyan-400" : "text-gray-300"}`} />
                            <p className={`text-[11px] ${passwordValidation.hasUpperCase ? "text-cyan-500 dark:text-cyan-400 font-semibold" : "text-gray-600 dark:text-gray-400"}`}>One uppercase letter</p>
                        </div>
                        <div className="flex items-center gap-x-1">
                            <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.hasLowerCase ? "text-cyan-500 dark:text-cyan-400" : "text-gray-300"}`} />
                            <p className={`text-[11px] ${passwordValidation.hasLowerCase ? "text-cyan-500 dark:text-cyan-400 font-semibold" : "text-gray-600 dark:text-gray-400"}`}>One lowercase letter</p>
                        </div>
                        <div className="flex items-center gap-x-1">
                            <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.hasNumberOrSpecialChar ? "text-cyan-500 dark:text-cyan-400" : "text-gray-300"}`} />
                            <p className={`text-[11px] ${passwordValidation.hasNumberOrSpecialChar ? "text-cyan-500 dark:text-cyan-400 font-semibold" : "text-gray-600 dark:text-gray-400"}`}>One number or special character</p>
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-x-2 ml-5">
                        <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="h-3 w-3 rounded cursor-pointer accent-gray-800 hover:accent-gray-900"
                        />
                        <p className="text-[11px] text-gray-600 dark:text-gray-400 text-left font-semibold">
                            I agree to the{" "}
                            <span className="text-cyan-500 dark:text-cyan-400 font-bold cursor-pointer" onClick={() => navigate("/terms-of-use")}>Terms of Use</span>
                            {" "}and our{" "}
                            <span className="text-cyan-500 dark:text-cyan-400 font-bold cursor-pointer" onClick={() => navigate("/privacy-policy")}>Privacy Policy</span>
                        </p>
                    </div>

                    <button
                        onClick={() => handleSubmitStep2()}
                        disabled={!isPasswordValid || !termsAccepted || loading}
                        className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black rounded-xl w-full my-6 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loading ? (
                            <BeatLoader size={10} color={"#000"} loading={loading} />
                        ) : (
                            "Create Account"
                        )}
                    </button>
                </div>
            )}

            {/* Step 4: OTP Verification */}
            {step === 4 && (
                <div className="p-4">
                    <p className="text-[18px] font-semibold text-black dark:text-white">Verify your account</p>
                    <p className="text-[11px] mt-1 font-semibold text-gray-500 dark:text-gray-400">
                        We sent a 6-digit code to {US_COUNTRY_CODE}{phoneNumber}
                    </p>

                    <div className="mt-5">
                        <div className="flex justify-between gap-2">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (otpInputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    onPaste={handleOtpPaste}
                                    disabled={otpTimer === 0}
                                    className="w-10 h-10 text-center text-[18px] font-semibold border-2 border-gray-400 rounded-lg outline-none focus:ring-1 focus:ring-[#BDE6FF] focus:border-[#BDE6FF] transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 text-center">
                        {otpTimer > 0 ? (
                            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                                Code expires in:{" "}
                                <span className={otpTimer <= 60 ? "text-red-500 dark:text-red-400" : "text-cyan-600 dark:text-cyan-400"}>
                                    {formatTime(otpTimer)}
                                </span>
                            </p>
                        ) : (
                            <p className="text-[11px] font-semibold text-red-500 dark:text-red-400">Code has expired!</p>
                        )}
                    </div>

                    <div className="mt-4 text-center">
                        <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">
                            Didn't receive a code?{" "}
                            <button
                                onClick={handleResendCode}
                                className="ml-1 text-cyan-500 hover:text-cyan-600 dark:text-cyan-400 dark:hover:text-cyan-300 underline cursor-pointer"
                            >
                                Resend
                            </button>
                        </p>
                    </div>

                    <button
                        onClick={handleVerifyOtp}
                        disabled={otpTimer === 0 || otp.some((digit) => !digit) || loading}
                        className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black rounded-xl w-full mt-6 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loading ? (
                            <BeatLoader size={10} color={"#fff"} loading={loading} />
                        ) : (
                            "Confirm"
                        )}
                    </button>

                    <div className="mt-4 text-center">
                        <button
                            onClick={handleChangePhone}
                            className="text-[12px] text-cyan-500 dark:text-cyan-400 hover:text-cyan-600 dark:hover:text-cyan-500 underline font-semibold"
                        >
                            Change Phone Number
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateAccountWithPhone;